import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { supabase } from '../services/supabase'
import Card from './Card'
import Button from './Button'
import {
  Upload,
  Download,
  FileSpreadsheet,
  FileText,
  CheckCircle2,
  AlertCircle,
  X,
} from 'lucide-react'
import {
  downloadExaminationCsvTemplate,
  exportExaminationsToCSV,
  importExaminationsFromCsv,
  parseExaminationCsvFile,
} from '../services/examinationImportExport'

/**
 * CSV import/export for examination + client fields.
 * Use offline / on another computer, then upload when the app is available.
 */
const ExaminationImportExport = ({ clientId = null, onImportComplete, onClose }) => {
  const organizationId = useOrganizationId()
  const fileInputRef = useRef(null)
  const [userId, setUserId] = useState(null)
  const [activeTab, setActiveTab] = useState('export')
  const [importFile, setImportFile] = useState(null)
  const [preview, setPreview] = useState([])
  const [importing, setImporting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [results, setResults] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    const loadUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUserId(user?.id || null)
    }
    loadUser()
  }, [])

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setError('')
    setResults(null)
    setImportFile(file)
    try {
      const rows = await parseExaminationCsvFile(file)
      setPreview(rows.slice(0, 3))
    } catch (err) {
      setError(err.message || 'Could not parse CSV')
      setImportFile(null)
      setPreview([])
    }
  }

  const handleExport = async () => {
    if (!organizationId) return
    try {
      setExporting(true)
      setError('')
      const result = await exportExaminationsToCSV(organizationId, clientId)
      if (result.count === 0) {
        setError('No examinations found to export.')
      }
    } catch (err) {
      setError(err.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  const handleImport = async () => {
    if (!organizationId || !importFile) return
    try {
      setImporting(true)
      setError('')
      const importResults = await importExaminationsFromCsv(
        organizationId,
        importFile,
        userId
      )
      setResults(importResults)
      if (importResults.imported > 0 && onImportComplete) {
        onImportComplete(importResults)
      }
    } catch (err) {
      setError(err.message || 'Import failed')
    } finally {
      setImporting(false)
    }
  }

  const resetImport = () => {
    setImportFile(null)
    setPreview([])
    setResults(null)
    setError('')
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  return (
    <Card variant="soft" className="p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Examination CSV Transfer
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Export or import client + examination fields for offline use on another computer.
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-full bg-gray-100 text-gray-500 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4">
        <Button
          type="button"
          variant={activeTab === 'export' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('export')}
        >
          Export
        </Button>
        <Button
          type="button"
          variant={activeTab === 'import' ? 'primary' : 'secondary'}
          onClick={() => setActiveTab('import')}
        >
          Import
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {activeTab === 'export' && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Downloads a CSV with the same fields as the examination form (client details, history,
            lifestyle, goals, vitals, and questionnaire JSON).
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={handleExport}
              disabled={exporting || !organizationId}
              className="flex items-center gap-2"
            >
              <Download size={16} />
              {exporting ? 'Exporting…' : clientId ? 'Export this client’s exams' : 'Export all exams'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => downloadExaminationCsvTemplate()}
              className="flex items-center gap-2"
            >
              <FileSpreadsheet size={16} />
              Download blank template
            </Button>
          </div>
        </div>
      )}

      {activeTab === 'import' && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Upload a CSV created from the template or from an export. Matching clients (same name +
            date of birth) are reused; otherwise a new client is created.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={handleFileSelect}
          />

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2"
            >
              <Upload size={16} />
              Choose CSV file
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => downloadExaminationCsvTemplate()}
              className="flex items-center gap-2"
            >
              <FileText size={16} />
              Template
            </Button>
            {importFile && (
              <Button type="button" variant="secondary" onClick={resetImport}>
                Clear
              </Button>
            )}
          </div>

          {importFile && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-gray-200 dark:border-gray-700 p-3"
            >
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {importFile.name}
              </p>
              {preview.length > 0 && (
                <div className="overflow-x-auto text-xs text-gray-600 dark:text-gray-300 mb-3">
                  <p className="mb-1 font-semibold">Preview (first rows)</p>
                  <pre className="whitespace-pre-wrap break-all bg-gray-50 dark:bg-gray-900/40 p-2 rounded-lg max-h-40 overflow-y-auto">
                    {JSON.stringify(preview, null, 2)}
                  </pre>
                </div>
              )}
              <Button
                type="button"
                onClick={handleImport}
                disabled={importing}
                className="flex items-center gap-2"
              >
                <Upload size={16} />
                {importing ? 'Importing…' : 'Import examinations'}
              </Button>
            </motion.div>
          )}

          {results && (
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
              <div className="flex items-center gap-2 font-semibold mb-1">
                <CheckCircle2 size={16} />
                Import finished
              </div>
              <p>
                Imported {results.imported} of {results.total} row(s)
                {results.skipped ? ` · skipped ${results.skipped}` : ''}
              </p>
              {results.errors?.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-red-700">
                  {results.errors.slice(0, 8).map((err) => (
                    <li key={`${err.row}-${err.error}`}>
                      Row {err.row}: {err.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

export default ExaminationImportExport
